import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useScript } from '../context/ScriptContext';
import { getSetting, updateSetting } from '../services/api';

type SiteMode = '' | 'live' | 'coming_soon' | 'maintenance';

interface AdminBadgeProps {
  onSiteModeChange?: (mode: string) => void;
  onProcurementsChange?: (hide: boolean) => void;
  onUsefulTextsChange?: (hide: boolean) => void;
}

const AdminBadge: React.FC<AdminBadgeProps> = ({ onSiteModeChange, onProcurementsChange, onUsefulTextsChange }) => {
  const { isAdmin, token, logout } = useAuth();
  const { t } = useScript();
  const [panelOpen, setPanelOpen] = useState(false);
  const [siteMode, setSiteMode] = useState<SiteMode>('');
  const [hideProcurements, setHideProcurements] = useState<boolean>(false);
  const [hideUsefulTexts, setHideUsefulTexts] = useState<boolean>(false);
  const [savingProcurements, setSavingProcurements] = useState<boolean>(false);
  const [savingUsefulTexts, setSavingUsefulTexts] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load current site_mode when panel opens
  useEffect(() => {
    if (panelOpen && isAdmin) {
      Promise.all([
        getSetting('site_mode').catch(() => ({ value: '' })),
        getSetting('hide_procurements').catch(() => ({ value: 'false' })),
        getSetting('hide_useful_texts').catch(() => ({ value: 'false' }))
      ]).then(([modeRes, procRes, textsRes]) => {
        setSiteMode((modeRes.value as SiteMode) || '');
        setHideProcurements(procRes.value === 'true');
        setHideUsefulTexts(textsRes.value === 'true');
      });
    }
  }, [panelOpen, isAdmin]);

  const handleSave = useCallback(async () => {
    if (!token) return;
    setSaving(true);
    try {
      await updateSetting(token, 'site_mode', siteMode);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (onSiteModeChange) onSiteModeChange(siteMode);
    } catch {
      // Ignore errors
    } finally {
      setSaving(false);
    }
  }, [token, siteMode, onSiteModeChange]);

  const handleToggleProcurements = useCallback(async (checked: boolean) => {
    if (!token) return;
    // Optimistic UI update
    setHideProcurements(checked);
    if (onProcurementsChange) onProcurementsChange(checked);
    
    setSavingProcurements(true);
    try {
      await updateSetting(token, 'hide_procurements', checked ? 'true' : 'false');
    } catch {
      // Revert on error
      setHideProcurements(!checked);
      if (onProcurementsChange) onProcurementsChange(!checked);
      alert(t("Дошло је до грешке при чувању подешавања."));
    } finally {
      setSavingProcurements(false);
    }
  }, [token, onProcurementsChange, t]);

  const handleToggleUsefulTexts = useCallback(async (checked: boolean) => {
    if (!token) return;
    // Optimistic UI update
    setHideUsefulTexts(checked);
    if (onUsefulTextsChange) onUsefulTextsChange(checked);
    
    setSavingUsefulTexts(true);
    try {
      await updateSetting(token, 'hide_useful_texts', checked ? 'true' : 'false');
    } catch {
      // Revert on error
      setHideUsefulTexts(!checked);
      if (onUsefulTextsChange) onUsefulTextsChange(!checked);
      alert(t("Дошло је до грешке при чувању подешавања."));
    } finally {
      setSavingUsefulTexts(false);
    }
  }, [token, onUsefulTextsChange, t]);

  if (!isAdmin) return null;

  return (
    <>
      <style>{`
        .ios-toggle {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
          flex-shrink: 0;
        }
        .ios-toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .ios-toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: .3s;
          border-radius: 24px;
        }
        .ios-toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: .3s;
          border-radius: 50%;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }
        .ios-toggle input:checked + .ios-toggle-slider {
          background-color: #c5a059;
        }
        .ios-toggle input:focus + .ios-toggle-slider {
          box-shadow: 0 0 1px #c5a059;
        }
        .ios-toggle input:checked + .ios-toggle-slider:before {
          transform: translateX(20px);
        }
        .ios-toggle.disabled .ios-toggle-slider {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .admin-toggle-row {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-top: 10px;
          padding: 12px 14px;
          background: rgba(0,0,0,0.02);
          border-radius: 8px;
          border: 1px solid rgba(0,0,0,0.06);
          transition: background 0.2s;
        }
        .admin-toggle-row:hover {
          background: rgba(0,0,0,0.04);
        }
      `}</style>
      <button
        className="admin-fixed"
        onClick={() => setPanelOpen(!panelOpen)}
        aria-expanded={panelOpen}
        aria-label={t("Административни панел")}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
        ADMIN
      </button>

      {panelOpen && (
        <>
          <div
            className="admin-panel-backdrop"
            onClick={() => setPanelOpen(false)}
          />
          <div className="admin-panel" role="dialog" aria-label={t("Подешавања сајта")}>
            <div className="admin-panel-header">
              <h3>{t("Подешавања сајта")}</h3>
              <button
                className="admin-panel-close"
                onClick={() => setPanelOpen(false)}
                aria-label={t("Затвори")}
              >
                ×
              </button>
            </div>

            <div className="admin-panel-body">
              <div className="admin-panel-section">
                <h4>{t("Режим рада сајта")}</h4>
                <p className="admin-panel-hint">
                  {t("Изаберите режим у ком ће сајт бити видљив посетиоцима.")}
                </p>

                <label className={`admin-radio ${siteMode === '' || siteMode === 'live' ? 'admin-radio-active' : ''}`}>
                  <input
                    type="radio"
                    name="site_mode"
                    value=""
                    checked={siteMode === '' || siteMode === 'live'}
                    onChange={() => setSiteMode('')}
                  />
                  <div className="admin-radio-content">
                    <span className="admin-radio-label">{t("Сајт активан")}</span>
                    <span className="admin-radio-desc">{t("Нормалан рад — посетиоци виде цео сајт.")}</span>
                  </div>
                </label>

                <label className={`admin-radio ${siteMode === 'coming_soon' ? 'admin-radio-active' : ''}`}>
                  <input
                    type="radio"
                    name="site_mode"
                    value="coming_soon"
                    checked={siteMode === 'coming_soon'}
                    onChange={() => setSiteMode('coming_soon')}
                  />
                  <div className="admin-radio-content">
                    <span className="admin-radio-label">{t("Ускоро долази")}</span>
                    <span className="admin-radio-desc">{t('Приказује страницу \u201EУскоро долази сајт\u201C свим посетиоцима.')}</span>
                  </div>
                </label>

                <label className={`admin-radio ${siteMode === 'maintenance' ? 'admin-radio-active' : ''}`}>
                  <input
                    type="radio"
                    name="site_mode"
                    value="maintenance"
                    checked={siteMode === 'maintenance'}
                    onChange={() => setSiteMode('maintenance')}
                  />
                  <div className="admin-radio-content">
                    <span className="admin-radio-label">{t("Одржавање")}</span>
                    <span className="admin-radio-desc">{t('Приказује страницу \u201EСајт се тренутно одржава\u201C свим посетиоцима.')}</span>
                  </div>
                </label>
              </div>

              <div className="admin-panel-section" style={{ marginTop: '20px' }}>
                <h4>{t("Остала подешавања")}</h4>
                <div className="admin-toggle-row">
                  <label className={`ios-toggle ${savingProcurements ? 'disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={hideProcurements}
                      onChange={(e) => handleToggleProcurements(e.target.checked)}
                      disabled={savingProcurements}
                    />
                    <span className="ios-toggle-slider"></span>
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="admin-radio-label" style={{ fontSize: '0.95rem', fontWeight: 600 }}>{t("Сакриј секцију 'Јавне набавке'")}</span>
                    <span className="admin-radio-desc" style={{ marginTop: '3px' }}>{t("Уклони линкове и онемогући приступ страници '/isporuke'")}</span>
                  </div>
                </div>

                <div className="admin-toggle-row">
                  <label className={`ios-toggle ${savingUsefulTexts ? 'disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={hideUsefulTexts}
                      onChange={(e) => handleToggleUsefulTexts(e.target.checked)}
                      disabled={savingUsefulTexts}
                    />
                    <span className="ios-toggle-slider"></span>
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="admin-radio-label" style={{ fontSize: '0.95rem', fontWeight: 600 }}>{t("Сакриј секцију 'Корисни текстови'")}</span>
                    <span className="admin-radio-desc" style={{ marginTop: '3px' }}>{t("Уклони линкове ка Корисним текстовима")}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="admin-panel-footer">
              <button
                className="btn-secondary admin-logout-btn"
                onClick={() => { setPanelOpen(false); logout(); }}
              >
                {t("Одјави се")}
              </button>
              <span className="admin-panel-footer-spacer" />
              {saved && <span className="admin-saved-msg">{t("Сачувано ✓")}</span>}
              <button
                className="btn-primary admin-save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? t("Чување...") : t("Сачувај")}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AdminBadge;
